/**
 * Сессионный снапшот каталога гардероба (`ClothingList`): вещи, страница, поиск,
 * фильтры и позиция прокрутки на момент ухода со страницы. Живёт в sessionStorage
 * с TTL, чтобы возврат с карточки вещи попадал в те же результаты (QA-7).
 *
 * Здесь — только чтение снапшота и сборка query для возврата: карточка вещи не должна
 * знать внутреннюю форму состояния списка.
 */
import {
    buildCatalogParams,
    emptyListFilters,
    hasAnyListFilter,
    LIST_FILTER_KEYS,
    ListFilters,
} from './catalogFilters';

export const WARDROBE_SESSION_KEY = 'clothes-wardrobe-session';
export const WARDROBE_SESSION_TTL_MS = 5 * 60 * 1000;

/**
 * Подмножество снапшота, нужное для восстановления отбора в URL. Фильтры-множества
 * лежат плоско (`categoryIds`, `colorIds`, …) — так же, как их пишет каталог.
 */
export type WardrobeSessionContext = {
    search?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    hasDefects?: boolean;
    waterProof?: boolean | null;
    notWornDays?: number | null;
    attention?: boolean;
    ownerKey?: string;
    timestamp: number;
} & Partial<ListFilters>;

/** Свежий снапшот или null (нет, протух, битый JSON, недоступный storage). */
export function readWardrobeSession(now: number = Date.now()): WardrobeSessionContext | null {
    try {
        const raw = sessionStorage.getItem(WARDROBE_SESSION_KEY);
        if (!raw) return null;
        const saved = JSON.parse(raw) as WardrobeSessionContext;
        if (typeof saved?.timestamp !== 'number') return null;
        if (now - saved.timestamp >= WARDROBE_SESSION_TTL_MS) return null;
        return saved;
    } catch {
        return null;
    }
}

/** Фильтры-множества снапшота в виде полного набора (отсутствующие ключи — пустые). */
function sessionListFilters(ctx: WardrobeSessionContext): ListFilters {
    const lists = emptyListFilters();
    LIST_FILTER_KEYS.forEach(key => {
        const values = ctx[key];
        if (Array.isArray(values)) lists[key] = values;
    });
    return lists;
}

/**
 * Query-строка для возврата в каталог с сохранённым отбором: `?search=…&sortBy=…`.
 * Пустая строка, если восстанавливать нечего. Дефолтная сортировка не пишется —
 * URL остаётся чистым, когда пользователь ничего не менял.
 */
export function wardrobeReturnQuery(ctx: WardrobeSessionContext | null): string {
    if (!ctx) return '';
    const qs = buildCatalogParams({
        search: ctx.search,
        sortBy: ctx.sortBy,
        sortDir: ctx.sortDir,
        lists: sessionListFilters(ctx),
        hasDefects: ctx.hasDefects,
        waterProof: ctx.waterProof ?? null,
        notWornDays: ctx.notWornDays ?? null,
        attention: ctx.attention,
    }).toString();
    return qs ? `?${qs}` : '';
}

/** Есть ли в снапшоте активный отбор (поиск/фильтры) — тогда уместна кнопка «Назад к результатам». */
export function hasWardrobeSearchContext(ctx: WardrobeSessionContext | null): boolean {
    if (!ctx) return false;
    return !!ctx.search
        || hasAnyListFilter(sessionListFilters(ctx))
        || !!ctx.hasDefects
        || ctx.waterProof != null
        || (ctx.notWornDays != null && ctx.notWornDays > 0)
        || !!ctx.attention;
}
